import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../apiClient';
import { FaTimes, FaUsers, FaPlus, FaEdit, FaTrash, FaUserPlus, FaUserMinus, FaRobot, FaLightbulb, FaExclamationTriangle } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import './StakeholderGroupModal.css';

function StakeholderGroupModal({ isOpen, onClose, apiBaseUrl }) {
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Group form state
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    group_type: 'custom',
    influence_level: '',
    interest_level: '',
    engagement_strategy: '',
    communication_preference: ''
  });

  // Member management state
  const [showMemberManagement, setShowMemberManagement] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [availableStakeholders, setAvailableStakeholders] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');

  // AI Analysis state
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [aiAnalysisResult, setAIAnalysisResult] = useState(null);
  const [analyzingGroupId, setAnalyzingGroupId] = useState(null);

  // Options
  const groupTypes = ['custom', 'department', 'location', 'role'];
  const influenceLevels = ['high', 'medium', 'low'];
  const interestLevels = ['high', 'medium', 'low'];
  const communicationPreferences = ['email', 'meetings', 'reports', 'phone', 'teams', 'slack'];

  const fetchGroups = useCallback(async () => {
    try {
      const response = await apiClient.get('/stakeholder_groups');
      setGroups(response.data || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load stakeholder groups.');
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get('/projects');
      // The projects API returns an array directly, not nested in a projects property
      setProjects(response.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]); // Set empty array on error to prevent undefined issues
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchGroups();
      fetchProjects();
    }
  }, [isOpen, fetchGroups, fetchProjects]);

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupFormData({
      name: '',
      description: '',
      project_id: '',
      group_type: 'custom',
      influence_level: '',
      interest_level: '',
      engagement_strategy: '',
      communication_preference: ''
    });
    setShowGroupForm(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name || '',
      description: group.description || '',
      project_id: group.project_id || '',
      group_type: group.group_type || 'custom',
      influence_level: group.influence_level || '',
      interest_level: group.interest_level || '',
      engagement_strategy: group.engagement_strategy || '',
      communication_preference: group.communication_preference || ''
    });
    setShowGroupForm(true);
  };

  const handleDeleteGroup = async (group) => {
    if (window.confirm(`Are you sure you want to delete the group "${group.name}"? This action cannot be undone.`)) {
      try {
        await apiClient.delete(`/stakeholder_groups/${group.id}`);
        fetchGroups();
      } catch (err) {
        console.error('Error deleting group:', err);
        setError('Failed to delete group. Please try again.');
      }
    }
  };

  const handleGroupFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = editingGroup 
        ? `/stakeholder_groups/${editingGroup.id}` 
        : '/stakeholder_groups';
      const method = editingGroup ? 'put' : 'post';

      await apiClient[method](endpoint, groupFormData);
      
      setShowGroupForm(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (err) {
      console.error('Error saving group:', err);
      setError(err.response?.data?.error || 'Failed to save group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageMembers = async (group) => {
    setSelectedGroup(group);
    setLoading(true);
    
    try {
      // Fetch group details with members
      const response = await apiClient.get(`/stakeholder_groups/${group.id}`);
      const groupData = response.data;
      setGroupMembers(groupData.members || []);
      
      // Fetch stakeholders for group membership
      const stakeholdersResponse = await apiClient.get('/v2/stakeholders');
      const allStakeholders = stakeholdersResponse.data.stakeholders || [];
      
      // Filter available stakeholders (not already in group)
      const memberStakeholderIds = new Set((groupData.members || []).map(m => m.stakeholder?.id || m.stakeholder_id));
      setAvailableStakeholders(allStakeholders.filter(stakeholder => !memberStakeholderIds.has(stakeholder.id)));
      
      setShowMemberManagement(true);
    } catch (err) {
      console.error('Error fetching group members:', err);
      setError('Failed to load group members.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (stakeholder) => {
    try {
      const currentMemberIds = groupMembers.map(m => m.stakeholder?.id || m.stakeholder_id);
      const newMemberIds = [...currentMemberIds, stakeholder.id];
      
      await apiClient.put(`/stakeholder_groups/${selectedGroup.id}/members`, {
        stakeholder_ids: newMemberIds
      });
      
      // Refresh member lists
      handleManageMembers(selectedGroup);
    } catch (err) {
      console.error('Error adding member:', err);
      setError('Failed to add member to group.');
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      const currentMemberIds = groupMembers.map(m => m.stakeholder?.id || m.stakeholder_id);
      const newMemberIds = currentMemberIds.filter(id => id !== (member.stakeholder?.id || member.stakeholder_id));
      
      await apiClient.put(`/stakeholder_groups/${selectedGroup.id}/members`, {
        stakeholder_ids: newMemberIds
      });
      
      // Refresh member lists
      handleManageMembers(selectedGroup);
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member from group.');
    }
  };

  const handleGroupAIAnalysis = async (group) => {
    if (!group.member_count || group.member_count === 0) {
      setError('This group has no members to analyze. Add members first.');
      return;
    }

    setAnalyzingGroupId(group.id);
    setAIAnalysisResult(null);
    setShowAIAnalysisModal(true);
    
    try {
      const response = await apiClient.post(`/stakeholder_groups/${group.id}/ai-analysis`);
      setAIAnalysisResult({
        type: 'group',
        analysis: response.data.analysis,
        group: response.data.group,
        metadata: response.data.metadata
      });
    } catch (err) {
      console.error('Error analyzing stakeholder group:', err);
      setAIAnalysisResult({
        type: 'error',
        error: err.response?.data?.error || 'Failed to analyze stakeholder group. Please try again.'
      });
    } finally {
      setAnalyzingGroupId(null);
    }
  };

  const filteredAvailableStakeholders = availableStakeholders.filter(stakeholder => {
    const fullName = stakeholder.full_name || stakeholder.first_name + ' ' + stakeholder.last_name || '';
    const email = stakeholder.email || '';
    const jobTitle = stakeholder.job_title || '';
    const organization = stakeholder.organization || '';
    
    return fullName.toLowerCase().includes(memberSearch.toLowerCase()) ||
           email.toLowerCase().includes(memberSearch.toLowerCase()) ||
           jobTitle.toLowerCase().includes(memberSearch.toLowerCase()) ||
           organization.toLowerCase().includes(memberSearch.toLowerCase());
  });

  // Render AI Analysis Results
  const renderGroupAIAnalysisResult = (result) => {
    if (!result) return null;

    if (result.type === 'error') {
      return (
        <div style={{ color: 'red', fontWeight: 600, padding: '1.5rem' }}>
          {result.error}
        </div>
      );
    }

    const analysis = result.analysis;
    if (!analysis) return null;

    // Handle fallback text format
    if (analysis.format === 'text') {
      return (
        <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb' }}>
            <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>
              <HiSparkles style={{ marginRight: 8 }} />
              Group Analysis Results
            </h4>
            <div style={{ color: '#222', marginTop: 6, whiteSpace: 'pre-wrap' }}>{analysis.summary || 'No analysis available'}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {analysis.group_summary && (
          <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb' }}>
            <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>
              <FaUsers style={{ marginRight: 8 }} />
              Group Summary
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>{analysis.group_summary}</div>
          </div>
        )}

        {analysis.collective_concerns && analysis.collective_concerns.length > 0 && (
          <div style={{ background: '#fff7e6', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #eab308' }}>
            <h4 style={{ margin: 0, color: '#eab308', fontWeight: 700 }}>
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              Collective Concerns
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.collective_concerns.map((concern, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{concern}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.influence_dynamics && (
          <div style={{ background: '#e6f7f7', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #10b981' }}>
            <h4 style={{ margin: 0, color: '#10b981', fontWeight: 700 }}>
              <FaLightbulb style={{ marginRight: 8 }} />
              Influence Dynamics
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>{analysis.influence_dynamics}</div>
          </div>
        )}

        {analysis.engagement_opportunities && analysis.engagement_opportunities.length > 0 && (
          <div style={{ background: '#f3e8ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #a21caf' }}>
            <h4 style={{ margin: 0, color: '#a21caf', fontWeight: 700 }}>
              <FaUsers style={{ marginRight: 8 }} />
              Engagement Opportunities
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.engagement_opportunities.map((opportunity, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{opportunity}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.communication_strategy && (
          <div style={{ background: '#fffbe7', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #f59e42' }}>
            <h4 style={{ margin: 0, color: '#f59e42', fontWeight: 700 }}>
              <FaUsers style={{ marginRight: 8 }} />
              Communication Strategy
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>{analysis.communication_strategy}</div>
          </div>
        )}

        {analysis.potential_challenges && analysis.potential_challenges.length > 0 && (
          <div style={{ background: '#fef2f2', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #ef4444' }}>
            <h4 style={{ margin: 0, color: '#ef4444', fontWeight: 700 }}>
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              Potential Challenges
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.potential_challenges.map((challenge, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{challenge}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.leverage_points && analysis.leverage_points.length > 0 && (
          <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #22c55e' }}>
            <h4 style={{ margin: 0, color: '#22c55e', fontWeight: 700 }}>
              <FaLightbulb style={{ marginRight: 8 }} />
              Leverage Points
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.leverage_points.map((point, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{point}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.recommendations && analysis.recommendations.length > 0 && (
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #64748b' }}>
            <h4 style={{ margin: 0, color: '#64748b', fontWeight: 700 }}>
              <FaLightbulb style={{ marginRight: 8 }} />
              Recommendations
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.recommendations.map((recommendation, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{recommendation}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content stakeholder-group-modal">
        <div className="modal-header">
          <h3>
            <FaUsers /> Stakeholder Group Management
          </h3>
          <button className="close-btn" onClick={() => onClose(false)}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {/* Group Form */}
          {showGroupForm && (
            <div className="group-form-section">
              <div className="section-header">
                <h4>{editingGroup ? 'Edit Group' : 'Create New Group'}</h4>
                <button 
                  className="btn secondary small"
                  onClick={() => setShowGroupForm(false)}
                >
                  Cancel
                </button>
              </div>

              <form onSubmit={handleGroupFormSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="group-name">Group Name *</label>
                    <input
                      type="text"
                      id="group-name"
                      value={groupFormData.name}
                      onChange={(e) => setGroupFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="group-project">Project *</label>
                    <select
                      id="group-project"
                      value={groupFormData.project_id}
                      onChange={(e) => setGroupFormData(prev => ({ ...prev, project_id: e.target.value }))}
                      required
                      disabled={loading}
                    >
                      <option value="">Select Project</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="group-description">Description</label>
                  <textarea
                    id="group-description"
                    value={groupFormData.description}
                    onChange={(e) => setGroupFormData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={loading}
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="group-type">Group Type</label>
                    <select
                      id="group-type"
                      value={groupFormData.group_type}
                      onChange={(e) => setGroupFormData(prev => ({ ...prev, group_type: e.target.value }))}
                      disabled={loading}
                    >
                      {groupTypes.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="group-communication">Communication Preference</label>
                    <select
                      id="group-communication"
                      value={groupFormData.communication_preference}
                      onChange={(e) => setGroupFormData(prev => ({ ...prev, communication_preference: e.target.value }))}
                      disabled={loading}
                    >
                      <option value="">Select Preference</option>
                      {communicationPreferences.map(pref => (
                        <option key={pref} value={pref}>
                          {pref.charAt(0).toUpperCase() + pref.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="group-influence">Influence Level</label>
                    <select
                      id="group-influence"
                      value={groupFormData.influence_level}
                      onChange={(e) => setGroupFormData(prev => ({ ...prev, influence_level: e.target.value }))}
                      disabled={loading}
                    >
                      <option value="">Select Level</option>
                      {influenceLevels.map(level => (
                        <option key={level} value={level}>
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="group-interest">Interest Level</label>
                    <select
                      id="group-interest"
                      value={groupFormData.interest_level}
                      onChange={(e) => setGroupFormData(prev => ({ ...prev, interest_level: e.target.value }))}
                      disabled={loading}
                    >
                      <option value="">Select Level</option>
                      {interestLevels.map(level => (
                        <option key={level} value={level}>
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="group-engagement">Engagement Strategy</label>
                  <textarea
                    id="group-engagement"
                    value={groupFormData.engagement_strategy}
                    onChange={(e) => setGroupFormData(prev => ({ ...prev, engagement_strategy: e.target.value }))}
                    disabled={loading}
                    rows="3"
                    placeholder="Describe how to engage with this group..."
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingGroup ? 'Update Group' : 'Create Group')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Member Management */}
          {showMemberManagement && selectedGroup && (
            <div className="member-management-section">
              <div className="section-header">
                <h4>Manage Members - {selectedGroup.name}</h4>
                <button 
                  className="btn secondary small"
                  onClick={() => setShowMemberManagement(false)}
                >
                  Back to Groups
                </button>
              </div>

              <div className="member-management-content">
                <div className="members-column">
                  <h5>Current Members ({groupMembers.length})</h5>
                  <div className="members-list">
                    {groupMembers.length === 0 ? (
                      <p className="empty-message">No members in this group yet.</p>
                    ) : (
                      groupMembers.map(member => {
                        // Use stakeholder data from new system
                        const stakeholder = member.stakeholder;
                        const displayName = stakeholder?.full_name || stakeholder?.first_name + ' ' + stakeholder?.last_name || member.employee?.name || 'Unknown';
                        const displayTitle = stakeholder?.job_title || member.employee?.job_position || 'No title';
                        const displayOrg = stakeholder?.organization || member.employee?.department || '';
                        
                        return (
                          <div key={member.id} className="member-item">
                            <div className="member-info">
                              <strong>{displayName}</strong>
                              <small>{displayTitle}{displayOrg && ` | ${displayOrg}`}</small>
                              {member.role_in_group && (
                                <span className="role-badge">{member.role_in_group}</span>
                              )}
                            </div>
                            <button
                              className="btn icon danger"
                              onClick={() => handleRemoveMember(member)}
                              title="Remove from group"
                            >
                              <FaUserMinus />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="available-column">
                  <h5>Available Stakeholders</h5>
                  <div className="search-input-group">
                    <input
                      type="text"
                      placeholder="Search stakeholders..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                    />
                  </div>
                  <div className="members-list">
                    {filteredAvailableStakeholders.length === 0 ? (
                      <p className="empty-message">
                        {memberSearch ? 'No stakeholders match your search.' : 'All stakeholders are already members.'}
                      </p>
                    ) : (
                      filteredAvailableStakeholders.map(stakeholder => (
                        <div key={stakeholder.id} className="member-item">
                          <div className="member-info">
                            <strong>{stakeholder.full_name || stakeholder.first_name + ' ' + stakeholder.last_name}</strong>
                            <small>{stakeholder.job_title}{stakeholder.organization && ` | ${stakeholder.organization}`}</small>
                          </div>
                          <button
                            className="btn icon primary"
                            onClick={() => handleAddMember(stakeholder)}
                            title="Add to group"
                          >
                            <FaUserPlus />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Groups List */}
          {!showGroupForm && !showMemberManagement && (
            <div className="groups-list-section">
              <div className="section-header">
                <h4>Stakeholder Groups</h4>
                <button 
                  className="btn primary"
                  onClick={handleCreateGroup}
                >
                  <FaPlus /> Create Group
                </button>
              </div>

              <div className="groups-list">
                {groups.length === 0 ? (
                  <div className="empty-state">
                    <FaUsers className="empty-icon" />
                    <h5>No Stakeholder Groups</h5>
                    <p>Create your first stakeholder group to organize stakeholders by project, role, or other criteria.</p>
                    <button className="btn primary" onClick={handleCreateGroup}>
                      Create First Group
                    </button>
                  </div>
                ) : (
                  groups.map(group => (
                    <div key={group.id} className="group-item">
                      <div className="group-info">
                        <div className="group-header">
                          <h5>{group.name}</h5>
                          <div className="group-meta">
                            <span className="member-count">{group.member_count} members</span>
                            <span className="group-type">{group.group_type}</span>
                          </div>
                        </div>
                        {group.description && (
                          <p className="group-description">{group.description}</p>
                        )}
                        <div className="group-details">
                          {group.influence_level && (
                            <span className="detail-badge influence">
                              Influence: {group.influence_level}
                            </span>
                          )}
                          {group.interest_level && (
                            <span className="detail-badge interest">
                              Interest: {group.interest_level}
                            </span>
                          )}
                          {group.communication_preference && (
                            <span className="detail-badge communication">
                              Prefers: {group.communication_preference}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="group-actions">
                        <button
                          className="btn secondary small"
                          onClick={() => handleManageMembers(group)}
                          title="Manage Members"
                        >
                          <FaUsers /> Members
                        </button>
                        <button
                          className="btn secondary small ai-btn"
                          onClick={() => handleGroupAIAnalysis(group)}
                          title="AI Analysis"
                          disabled={analyzingGroupId === group.id || !group.member_count}
                        >
                          <HiSparkles />
                        </button>
                        <button
                          className="btn secondary small"
                          onClick={() => handleEditGroup(group)}
                          title="Edit Group"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn danger small"
                          onClick={() => handleDeleteGroup(group)}
                          title="Delete Group"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn secondary" onClick={() => onClose(false)}>
            Close
          </button>
        </div>
      </div>

      {/* AI Analysis Modal */}
      {showAIAnalysisModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 800, zIndex: 1001 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                <FaRobot style={{ marginRight: 8, color: '#2563eb' }} />
                AI Stakeholder Group Analysis
                {aiAnalysisResult?.group && (
                  <div style={{ fontSize: '0.9rem', fontWeight: 400, color: '#666', marginTop: '0.25rem' }}>
                    Group: {aiAnalysisResult.group.name} ({aiAnalysisResult.metadata?.stakeholders_analyzed || 0} members)
                  </div>
                )}
              </h3>
              <button className="close-button" onClick={() => setShowAIAnalysisModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {aiAnalysisResult ? (
                renderGroupAIAnalysisResult(aiAnalysisResult)
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <HiSparkles style={{ fontSize: '3rem', color: '#2563eb', marginBottom: '1rem' }} />
                  <p>Analyzing stakeholder group dynamics...</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowAIAnalysisModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StakeholderGroupModal; 